import { Popover, Transition } from "@headlessui/react"
import { Settings2 } from "lucide-react"
import { Fragment } from "react"

// Define a generic interface for the options prop
interface Option<T> {
  id: T
  name: string
}

interface DropdownFilterProps<T> {
  value: T[]
  options: readonly Option<T>[]
  hideLabel?: boolean
  popoverPlace?: "left" | "right"
  popoverPlaceInMobile?: "left" | "right"
  className?: string
  onChange: (value: T) => void
}

const DropdownFilter = <T extends string>({
  value,
  hideLabel = false,
  popoverPlace = "right",
  popoverPlaceInMobile = "right",
  className = "",
  options,
  onChange
}: DropdownFilterProps<T>) => {
  const filterByLabel = options.filter(option => value.includes(option.id))[0]
    ?.name

  return (
    <Popover
      as="div"
      className={`relative z-10 inline-block text-left ${className}`}
    >
      <Popover.Button className="group inline-flex items-center justify-center appearance-none px-3 py-2 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none hover:bg-primary-50 hover:border-primary-500 hover:text-primary-600 sm:text-sm">
        <Settings2
          size={16}
          className="flex-shrink-0 text-base-dark group-hover:text-primary-600 mr-2"
          aria-hidden="true"
        />
        <span className={hideLabel ? "sr-only" : ""}>Filters</span>
      </Popover.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Popover.Panel
          className={`origin-top-right absolute ${
            popoverPlaceInMobile === "left"
              ? "left-0 right-auto"
              : "left-auto right-0"
          } ${
            popoverPlace === "left"
              ? "lg:left-0 lg:right-auto"
              : "lg:left-auto lg:right-0"
          } mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none z-10`}
        >
          <h3 className="sr-only">Filter by {filterByLabel}</h3>
          <div className="space-y-4">
            {options.map((option, optionIdx) => (
              <label
                htmlFor={`filter-${option.id}-${optionIdx}`}
                key={optionIdx}
                className="flex items-center cursor-pointer"
              >
                <input
                  id={`filter-${option.id}-${optionIdx}`}
                  name={`${value}[]`}
                  defaultValue={value}
                  checked={value.includes(option.id)}
                  onChange={() => onChange(option.id)}
                  type="checkbox"
                  className="h-5 w-5 border-gray-300 rounded text-indigo-500 focus:ring-indigo-400"
                />
                <span className="ml-2 pr-6 text-gray-900 whitespace-nowrap">
                  {option.name}
                </span>
              </label>
            ))}
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}

export default DropdownFilter
